// Copyright (c) Docugami, Inc. All rights reserved.

import { useEffect, useRef, useState } from "react";

export enum ContentLoadState {
    None,
    Loading,
    Ready,
    Error
}

export enum ContentType {
    Script,
    StyleSheet
}

export const useDynamicHtmlContent = (type: ContentType, source: string, id?: string, attributes?: {[key: string] : string}): ContentLoadState => {
    const elementRef = useRef<HTMLLinkElement | HTMLScriptElement>();
    const [loadState, setLoadState] = useState(ContentLoadState.None);
    const componentRef = useRef(true);

    useEffect(() => {
        return () =>  {
            componentRef.current = false;
            elementRef.current?.remove();
        }
    }, []);

    useEffect(() => {
        elementRef.current?.remove();
        setLoadState(ContentLoadState.None);
    }, [type, source]);

    useEffect(() => {
        if (loadState !== ContentLoadState.None || !source) {
            return;
        }

        setLoadState(ContentLoadState.Loading);
        const elementType: keyof HTMLElementTagNameMap = type === ContentType.Script ? 'script' : 'link';
        const element = document.createElement(elementType);
        elementRef.current = element;

        if (type === ContentType.Script) {
            const script = element as HTMLScriptElement;
            script.async = true;
            script.src = source;
        }
        else if (type === ContentType.StyleSheet) {
            const link = element as HTMLLinkElement;
            link.rel = 'stylesheet';
            link.href = source;
        }

        if (!!id) {
            element.id = id;
        }

        if (!!attributes) {
            for (const [key, value] of Object.entries(attributes)) {
                element.setAttribute(key, value);
            }
        }

        element.onload = () => {
            if (componentRef.current) {
                setLoadState(ContentLoadState.Ready);
            }
        }

        element.onerror = () => {
            if (componentRef.current) {
                setLoadState(ContentLoadState.Error);
            }
        }

        const parent = document.head || document.body;
        parent.appendChild(element);
    }, // eslint-disable-next-line react-hooks/exhaustive-deps
        [loadState, type, source]);

    return loadState;
}
